import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Calendar, Download, ChevronDown, ChevronRight, Filter, RefreshCw, Loader2, Search, FileDown } from 'lucide-react';
import ConsistencyBadge from '../../components/ConsistencyBadge';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { haricleriAyikla } from '../../utils/haricKampanyalar';
import { useMetaReach, useReachOf, MetaReachContext, mrNew, mrAddRow, mrUnion, sumMetrics, withDedupReach } from '../../utils/metaReachDedup';
import { useSearchParams } from 'react-router-dom';
import { classifyCampaign } from '../../utils/brandingClassifier';

/* ── Bütçe kapsamı ─────────────────────────────────────────────────────
 * Yataş grubunda Marka ve Performans ekiplerinin bütçeleri ayrı; Marka ekibi
 * yalnız branding bütçesinden yapılan harcamayı görmek istiyor (26 Eki 2026).
 * Ayrım report-2/3 ile aynı ortak kuraldan: kampanya adındaki "Branding" /
 * "Performans" → yoksa Meta objective / Google kampanya tipi. Ham satırlar
 * toplanmadan ÖNCE süzülür, böylece alttaki tüm tablolar (Özet, Search,
 * Always On, Ana Kampanya) aynı kapsamı gösterir. Medya satın alma her zaman
 * branding; Criteo/RTB retargeting, adında sinyal yoksa performans. */
const SCOPES = [
  { key: 'all',         label: 'Tümü' },
  { key: 'performance', label: 'Performans' },
  { key: 'branding',    label: 'Branding' },
];
const SCOPE_PARAM = { performance: 'performans', branding: 'branding' };
const scopeFromParam = (v) => (v === 'performans' ? 'performance' : v === 'branding' ? 'branding' : 'all');

const COLUMNS = [
  { key: 'harcama',        label: 'Harcama',        format: 'currency' },
  { key: 'gosterim',       label: 'Gösterim',       format: 'number'   },
  { key: 'etkilesim',      label: 'Etkileşim',      format: 'number'   },
  { key: 'erisim',         label: 'Erişim',          format: 'number'   },
  { key: 'izlenme',        label: 'İzlenme',         format: 'number'   },
  { key: 'vcr',            label: 'VCR',             format: 'percent'  },
  { key: 'tiklama',        label: 'Tıklama',         format: 'number'   },
  { key: 'tekilKullanici', label: 'Tekil Kullanıcı', format: 'number'   },
  { key: 'trafik',         label: 'Trafik',          format: 'number'   },
  { key: 'storeVisit',     label: 'Store Visit',     format: 'number'   },
  { key: 'gelir',          label: 'Gelir',           format: 'currency' },
  { key: 'roas',           label: 'ROAS',            format: 'roas'     },
  { key: 'satis',          label: 'Satış',           format: 'number'   },
];

const TABLE_DATA = [
  {
    platform: 'Google Ads',
    rows: [
      { name: 'Youtube' },
      { name: 'Search' },
      { name: 'P-max Conv.' },
      { name: 'P-Max Branding' },
      { name: 'Demand Gen' },
      { name: 'Display / GDN' },
      { name: 'P-max Local' },
    ],
  },
  {
    platform: 'META',
    rows: [
      { name: 'Dönüşüm' },
      { name: 'Trafik' },
      { name: 'Bilinirlik' },
      { name: 'Etkileşim' },
      { name: 'Thruplay' },
      { name: 'Local - Store Visit' },
    ],
  },
  {
    platform: 'Pinterest',
    rows: [
      { name: 'Pinterest / Performans' },
      { name: 'Pinterest / Branding' },
    ],
  },
  {
    platform: 'TikTok',
    rows: [
      { name: 'TikTok' },
    ],
  },
  {
    platform: 'Remarketing',
    rows: [
      { name: 'Criteo' },
      { name: 'RTB' },
    ],
  },
  {
    platform: 'Media Satın Alma',
    rows: [
      { name: 'Display' },
      { name: 'Native' },
      { name: 'Video' },
      { name: 'Programatik' },
    ],
  },
];

/* ── Google Ads kampanya → satır eşleştirme ─────────────────────────── */

function classifyGoogleAdsCampaign(campaignType, campaignName) {
  const type = (campaignType || '').toLowerCase();
  const name = (campaignName || '').toLowerCase();

  if (type.includes('performance_max') || type === 'pmax') {
    if (name.includes('local store') || name.includes('lokal') || name.includes('local visit') || name.includes('store visit') || name.includes('mağaza'))
      return 'P-max Local';
    if (name.includes('branding') || name.includes('session start'))
      return 'P-Max Branding';
    return 'P-max Conv.';
  }
  if (type.includes('search'))        return 'Search';
  if (type.includes('video'))         return 'Youtube';
  if (type.includes('demand_gen'))    return 'Demand Gen';
  if (type.includes('display'))       return 'Display / GDN';
  if (type.includes('shopping'))      return 'Search';
  return null;
}

function aggregateGoogleAdsData(rawData) {
  const buckets = {};
  const campaignBuckets = {};
  const GA_ROWS = ['Youtube', 'Search', 'P-max Conv.', 'P-Max Branding', 'Demand Gen', 'Display / GDN', 'P-max Local'];
  GA_ROWS.forEach(r => {
    buckets[r] = { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 };
    campaignBuckets[r] = {};
  });

  (rawData || []).forEach(row => {
    const bucket = classifyGoogleAdsCampaign(row.campaignType, row.campaignName);
    if (!bucket || !buckets[bucket]) return;

    const cost = Number(row.googleAdsCost) || 0;
    const imp = Number(row.googleAdsImpression) || 0;
    const clicks = Number(row.googleAdsClicks) || 0;
    const traffic = Number(row.sessions) || 0;
    const revenue = Number(row.googleAdsRevenue) || 0;
    const sales = Number(row.googleAdsTransaction) || 0;

    buckets[bucket].harcama  += cost;
    buckets[bucket].gosterim += imp;
    buckets[bucket].tiklama  += clicks;
    buckets[bucket].trafik   += traffic;
    buckets[bucket].gelir    += revenue;
    buckets[bucket].satis    += sales;

    const cName = row.campaignName || 'Unknown';
    if (!campaignBuckets[bucket][cName]) {
      campaignBuckets[bucket][cName] = { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 };
    }
    campaignBuckets[bucket][cName].harcama  += cost;
    campaignBuckets[bucket][cName].gosterim += imp;
    campaignBuckets[bucket][cName].tiklama  += clicks;
    campaignBuckets[bucket][cName].trafik   += traffic;
    campaignBuckets[bucket][cName].gelir    += revenue;
    campaignBuckets[bucket][cName].satis    += sales;
  });

  const result = {};
  GA_ROWS.forEach(r => {
    const b = buckets[r];
    const hasData = Object.values(b).some(v => v > 0);
    if (hasData) {
      const campaigns = Object.entries(campaignBuckets[r])
        .map(([name, metrics]) => ({ name, ...metrics, roas: metrics.harcama > 0 ? metrics.gelir / metrics.harcama : 0 }))
        .sort((a, b) => b.harcama - a.harcama);
      b.roas = b.harcama > 0 ? b.gelir / b.harcama : 0;
      result[r] = { ...b, campaigns };
    }
  });
  return result;
}

/* ── Meta kampanya → satır eşleştirme (isim öncelikli, objective yedek) ── */

function classifyMetaCampaign(objective, campaignName) {
  const name = (campaignName || '').toLowerCase().replace(/[\s\-_]/g, '');
  const nameRaw = (campaignName || '').toLowerCase();

  // 1) Kampanya isminden sınıflandır (öncelikli)
  if (name.includes('videoview') || name.includes('thruplay'))
    return 'Thruplay';
  if (name.includes('storevisit') || name.includes('localstoreawareness'))
    return 'Local - Store Visit';
  if (nameRaw.includes('instagram gönderi') || nameRaw.includes('instagram post'))
    return 'Etkileşim';
  if (name.includes('postengagement') || (name.includes('engagement') && !name.includes('video')))
    return 'Etkileşim';
  if (name.includes('reach') || name.includes('brandawareness'))
    return 'Bilinirlik';
  if (name.includes('traffic') || name.includes('landingpageview') || name.includes('profilevisit'))
    return 'Trafik';

  // 2) Objective'den yedek sınıflandırma
  const obj = (objective || '').toUpperCase().trim();
  if (['OUTCOME_SALES', 'CONVERSIONS'].includes(obj))                         return 'Dönüşüm';
  if (['OUTCOME_TRAFFIC', 'TRAFFIC', 'LINK_CLICKS'].includes(obj))            return 'Trafik';
  if (['REACH'].includes(obj))                                                 return 'Bilinirlik';
  if (['OUTCOME_AWARENESS', 'AWARENESS', 'BRAND_AWARENESS'].includes(obj))     return 'Bilinirlik';
  if (['OUTCOME_ENGAGEMENT', 'ENGAGEMENT', 'POST_ENGAGEMENT'].includes(obj))   return 'Etkileşim';
  if (['VIDEO_VIEWS', 'THRUPLAY'].includes(obj))                               return 'Thruplay';
  if (['STORE_TRAFFIC', 'LOCAL_AWARENESS', 'STORE_VISITS'].includes(obj))      return 'Local - Store Visit';

  return null;
}

function aggregateMetaData(rawData) {
  const META_ROWS = ['Dönüşüm', 'Trafik', 'Bilinirlik', 'Etkileşim', 'Thruplay', 'Local - Store Visit'];
  const buckets = {};
  const campaignBuckets = {};
  META_ROWS.forEach(r => {
    buckets[r] = { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0, _mr: mrNew() };
    campaignBuckets[r] = {};
  });

  (rawData || []).forEach(row => {
    const objective = row.campaign_objective || row.campaignObjective;
    const cName = row.campaign_name || row.campaignName || 'Unknown';
    const bucket = classifyMetaCampaign(objective, cName);
    if (!bucket || !buckets[bucket]) return;

    const spend = Number(row.spend) || 0;
    const imp = Number(row.impressions) || 0;
    const clicks = Number(row.clicks) || 0;
    const actions = Number(row.actions) || 0;
    const reach = Number(row.reach) || 0;
    const view = Number(row.view || row.trueplay) || 0;
    const traffic = Number(row.ga4_sessions || row.ga4Sessions) || 0;
    const users = Number(row.ga4_users || row.ga4Users) || 0;
    const revenue = Number(row.conversion_value || row.conversionValue) || 0;
    const sales = Number(row.conversions) || 0;

    buckets[bucket].harcama        += spend;
    buckets[bucket].gosterim       += imp;
    buckets[bucket].tiklama        += clicks;
    buckets[bucket].etkilesim      += actions;
    buckets[bucket].erisim         += reach;
    buckets[bucket].izlenme        += view;
    buckets[bucket].trafik         += traffic;
    buckets[bucket].tekilKullanici += users;
    buckets[bucket].gelir          += revenue;
    buckets[bucket].satis          += sales;
    // Erişim toplanamaz — kampanya kimliği tutulur, tekil değer Meta'dan sorulur
    mrAddRow(buckets[bucket]._mr, row, { level: 'campaign' });

    if (!campaignBuckets[bucket][cName]) {
      campaignBuckets[bucket][cName] = { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0, _mr: mrNew() };
    }
    mrAddRow(campaignBuckets[bucket][cName]._mr, row, { level: 'campaign' });
    campaignBuckets[bucket][cName].harcama        += spend;
    campaignBuckets[bucket][cName].gosterim       += imp;
    campaignBuckets[bucket][cName].tiklama        += clicks;
    campaignBuckets[bucket][cName].etkilesim      += actions;
    campaignBuckets[bucket][cName].erisim         += reach;
    campaignBuckets[bucket][cName].izlenme       += view;
    campaignBuckets[bucket][cName].trafik         += traffic;
    campaignBuckets[bucket][cName].tekilKullanici += users;
    campaignBuckets[bucket][cName].gelir          += revenue;
    campaignBuckets[bucket][cName].satis          += sales;
  });

  const result = {};
  META_ROWS.forEach(r => {
    const b = buckets[r];
    const hasData = Object.values(b).some(v => v > 0);
    if (hasData) {
      const campaigns = Object.entries(campaignBuckets[r])
        .map(([name, metrics]) => ({ name, ...metrics, roas: metrics.harcama > 0 ? metrics.gelir / metrics.harcama : 0 }))
        .sort((a, b) => b.harcama - a.harcama);
      b.roas = b.harcama > 0 ? b.gelir / b.harcama : 0;
      result[r] = { ...b, campaigns };
    }
  });
  return result;
}

/* ── Always On (AO) — Google + Meta kırılımı ──────────────────────── */
/*
 * aggregateByKeyword now receives granular adgroup-level rows (Google) and
 * ad-level rows (Meta) so that matching is scoped to only the matched
 * adgroups/ads. Campaign-level metrics are never used here — this eliminates
 * the bug where matching one adgroup name pulled in the entire parent
 * campaign's spend.
 *
 * googleAdgroupRows — array from GET /api/google-ads/adgroup-performance
 *   Each row: { campaign_id, campaign_name, campaign_type, ad_group_id,
 *               ad_group_name, date, googleAdsCost, googleAdsImpression,
 *               googleAdsClicks, googleAdsTransaction, googleAdsRevenue,
 *               sessions: 0 }
 *
 * metaAdRows — array from GET /api/meta-ads/ad-performance
 *   Each row: { campaign_id, campaign_name, campaign_objective, adset_id,
 *               adset_name, ad_id, ad_name, date, spend, impressions,
 *               clicks, reach, view, trueplay, actions, conversions,
 *               conversion_value, ga4_sessions: 0, ga4_users: 0 }
 *
 * Eşleşme mantığı — KADEMELİ (ladder) + TAM AD eşleşme (kullanıcı kararı):
 *   Her keyword için en İNCE seviyeden başla, eşleşme yoksa bir ÜSTE düş:
 *     Google: ad_group_name  →  (yoksa) campaign_name        [ad seviyesi YOK]
 *     Meta:   ad_name  →  (yoksa) adset_name  →  (yoksa) campaign_name
 *   İlk EŞLEŞEN seviyede dur ("E" reklamı varsa onun verisi; yoksa içinde
 *   bulunduğu adgroup/adset toplamı; o da yoksa kampanya toplamı).
 *   Eşleşme TAM ad (normalize: trim + tek boşluk + tr-küçük harf), substring DEĞİL.
 *
 * gelir / satis artık PLATFORM-RAPORLU gösterilir (0 değil):
 *   Google adgroup → conversion_value/conversions, Meta ad → conversion_value/conversions.
 *   (GA4 atıfı değil; trafik granüler seviyede yok → 0 kalır.)
 *
 * Return shape eskisiyle aynı → render değişmez.
 */
// Tam-ad eşleşmesi için Türkçe-duyarlı normalize (İ/I tuzağı + boşluk sadeleştirme).
function normName(s) {
  return String(s || '').trim().replace(/\s+/g, ' ').toLocaleLowerCase('tr');
}

/*
 * Bir keyword listesini, alanları İNCE→KABA sırada verilen satır kümesine karşı
 * çözer. Her keyword için ilk EŞLEŞEN seviyedeki satırları seçer (fallback ladder).
 * Aynı satır birden çok keyword'e denk gelirse bir kez sayılır (Set).
 *  rows   : granüler satırlar
 *  fields : ['ad_name','adset_name','campaign_name'] gibi ince→kaba alan adları
 *  kwSet  : normalize edilmiş keyword'ler
 */
function resolveRowsByLadder(rows, fields, kwSet) {
  const selected = new Set();
  for (const kw of kwSet) {
    for (const field of fields) {
      const matched = (rows || []).filter(r => normName(r[field]) === kw);
      if (matched.length) { matched.forEach(r => selected.add(r)); break; } // bu seviyede bulundu → dur
    }
  }
  return selected;
}

function aggregateByKeyword(googleAdgroupRows, metaAdRows, keyword) {
  const keywords = Array.isArray(keyword) ? keyword : [keyword];
  const kwSet = keywords.map(normName).filter(Boolean);

  const result = {
    Google: { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0, campaigns: [] },
    Meta:   { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0, campaigns: [], _mr: mrNew() },
  };
  const gCamps = {};
  const mCamps = {};

  // Google ladder: ad_group_name → campaign_name (ad seviyesi yok)
  const gSelected = resolveRowsByLadder(googleAdgroupRows, ['ad_group_name', 'campaign_name'], kwSet);
  gSelected.forEach(row => {
    const cName = row.campaign_name || 'Unknown';
    const cost = Number(row.googleAdsCost) || 0;
    const imp = Number(row.googleAdsImpression) || 0;
    const clicks = Number(row.googleAdsClicks) || 0;
    const revenue = Number(row.googleAdsRevenue) || 0;       // platform conversion_value
    const sales = Number(row.googleAdsTransaction) || 0;     // platform conversions
    result.Google.harcama  += cost;
    result.Google.gosterim += imp;
    result.Google.tiklama  += clicks;
    result.Google.gelir    += revenue;
    result.Google.satis    += sales;
    // trafik (GA4 oturum) granüler seviyede yok → 0
    if (!gCamps[cName]) gCamps[cName] = { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 };
    gCamps[cName].harcama  += cost;
    gCamps[cName].gosterim += imp;
    gCamps[cName].tiklama  += clicks;
    gCamps[cName].gelir    += revenue;
    gCamps[cName].satis    += sales;
  });

  // Meta ladder: ad_name → adset_name → campaign_name
  const mSelected = resolveRowsByLadder(metaAdRows, ['ad_name', 'adset_name', 'campaign_name'], kwSet);
  mSelected.forEach(row => {
    const cName = row.campaign_name || 'Unknown';
    const spend = Number(row.spend) || 0;
    const imp = Number(row.impressions) || 0;
    const clicks = Number(row.clicks) || 0;
    const actions = Number(row.actions) || 0;
    const reach = Number(row.reach) || 0;
    const view = Number(row.view || row.trueplay) || 0;
    const revenue = Number(row.conversion_value) || 0;       // platform conversion_value
    const sales = Number(row.conversions) || 0;              // platform conversions
    result.Meta.harcama  += spend;
    result.Meta.gosterim += imp;
    result.Meta.tiklama  += clicks;
    result.Meta.etkilesim += actions;
    result.Meta.erisim   += reach;
    result.Meta.izlenme  += view;
    result.Meta.gelir    += revenue;
    result.Meta.satis    += sales;
    if (!mCamps[cName]) mCamps[cName] = { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0, _mr: mrNew() };
    // Reklam seviyesinde seçim → tekil erişim reklam kümesi üzerinden sorulur
    mrAddRow(result.Meta._mr, row, { level: 'ad' });
    mrAddRow(mCamps[cName]._mr, row, { level: 'ad' });
    mCamps[cName].harcama  += spend;
    mCamps[cName].gosterim += imp;
    mCamps[cName].tiklama  += clicks;
    mCamps[cName].etkilesim += actions;
    mCamps[cName].erisim   += reach;
    mCamps[cName].izlenme  += view;
    mCamps[cName].gelir    += revenue;
    mCamps[cName].satis    += sales;
  });

  result.Google.campaigns = Object.entries(gCamps)
    .map(([name, m]) => ({ name, ...m, roas: m.harcama > 0 ? m.gelir / m.harcama : 0 }))
    .sort((a, b) => b.harcama - a.harcama);
  result.Google.roas = result.Google.harcama > 0 ? result.Google.gelir / result.Google.harcama : 0;

  result.Meta.campaigns = Object.entries(mCamps)
    .map(([name, m]) => ({ name, ...m, roas: m.harcama > 0 ? m.gelir / m.harcama : 0 }))
    .sort((a, b) => b.harcama - a.harcama);
  result.Meta.roas = result.Meta.harcama > 0 ? result.Meta.gelir / result.Meta.harcama : 0;

  return result;
}

/* ── Google Search — Brand / Non-Brand kırılımı ───────────────────── */

function aggregateGoogleSearchData(rawData) {
  const SEARCH_ROWS = ['Pure Brand', 'Brand', 'Generic'];
  const buckets = {};
  const campaignBuckets = {};
  SEARCH_ROWS.forEach(r => {
    buckets[r] = { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 };
    campaignBuckets[r] = {};
  });

  (rawData || []).forEach(row => {
    const type = (row.campaignType || '').toLowerCase();
    if (!type.includes('search') && !type.includes('shopping')) return;

    const cName = row.campaignName || '';
    const nameLc = cName.toLowerCase();

    let bucket = null;
    if (nameLc.includes('pure brand'))       bucket = 'Pure Brand';
    else if (nameLc.includes('brand'))       bucket = 'Brand';
    else if (nameLc.includes('generic'))     bucket = 'Generic';
    if (!bucket) return;

    const cost = Number(row.googleAdsCost) || 0;
    const imp = Number(row.googleAdsImpression) || 0;
    const clicks = Number(row.googleAdsClicks) || 0;
    const traffic = Number(row.sessions) || 0;
    const revenue = Number(row.googleAdsRevenue) || 0;
    const sales = Number(row.googleAdsTransaction) || 0;

    buckets[bucket].harcama  += cost;
    buckets[bucket].gosterim += imp;
    buckets[bucket].tiklama  += clicks;
    buckets[bucket].trafik   += traffic;
    buckets[bucket].gelir    += revenue;
    buckets[bucket].satis    += sales;

    if (!campaignBuckets[bucket][cName]) {
      campaignBuckets[bucket][cName] = { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 };
    }
    campaignBuckets[bucket][cName].harcama  += cost;
    campaignBuckets[bucket][cName].gosterim += imp;
    campaignBuckets[bucket][cName].tiklama  += clicks;
    campaignBuckets[bucket][cName].trafik   += traffic;
    campaignBuckets[bucket][cName].gelir    += revenue;
    campaignBuckets[bucket][cName].satis    += sales;
  });

  const result = {};
  SEARCH_ROWS.forEach(r => {
    const b = buckets[r];
    const campaigns = Object.entries(campaignBuckets[r])
      .map(([name, metrics]) => ({ name, ...metrics, roas: metrics.harcama > 0 ? metrics.gelir / metrics.harcama : 0 }))
      .sort((a, b) => b.harcama - a.harcama);
    b.roas = b.harcama > 0 ? b.gelir / b.harcama : 0;
    result[r] = { ...b, campaigns };
  });
  return result;
}

/* ── Yardımcı fonksiyonlar ──────────────────────────────────────────── */

const formatValue = (value, format) => {
  if (value === null || value === undefined || value === '') return '–';
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency', currency: 'TRY',
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      }).format(value);
    case 'number':
      return new Intl.NumberFormat('tr-TR').format(value);
    case 'percent':
      return `%${(value * 100).toFixed(1)}`;
    case 'roas':
      return typeof value === 'number' && isFinite(value) ? `${value.toFixed(2)}x` : '–';
    default:
      return value;
  }
};

const fmtDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/* ── Alt bileşenler ─────────────────────────────────────────────────── */

function PlatformGroup({ group, data, collapsedPlatforms, togglePlatform, expandedRows, toggleRow }) {
  const isCollapsed = collapsedPlatforms.has(group.platform);
  const platformData = data?.[group.platform] || {};

  const reachOf = useReachOf();
  const platformTotal = useMemo(() => {
    // Erişim: Meta satırlarının kampanya kümeleri birleştirilip tekil sorulur
    const t = sumMetrics(group.rows.map(r => platformData[r.name]), COLUMNS, reachOf);
    if (t.harcama > 0 && t.gelir != null) t.roas = t.gelir / t.harcama;
    return t;
  }, [platformData, group.rows, reachOf]);

  return (
    <>
      <tr
        className="cursor-pointer select-none group/row"
        onClick={() => togglePlatform(group.platform)}
      >
        <td
          colSpan={2}
          className="sticky left-0 z-10 pl-3 pr-2 py-[7px] text-[11px] font-semibold text-slate-100 tracking-wide uppercase bg-slate-800 border-b border-slate-700/60"
        >
          <div className="flex items-center gap-1.5">
            {isCollapsed
              ? <ChevronRight size={11} className="text-slate-400" />
              : <ChevronDown size={11} className="text-slate-400" />}
            {group.platform}
          </div>
        </td>
        {COLUMNS.map(col => (
          <td
            key={col.key}
            className="px-2 py-[7px] text-right text-[10px] font-semibold text-slate-300 bg-slate-800 border-b border-slate-700/60 tabular-nums whitespace-nowrap"
          >
            {formatValue(platformTotal[col.key], col.format)}
          </td>
        ))}
      </tr>

      {!isCollapsed &&
        group.rows.map((row) => {
          const rd = platformData[row.name] || {};
          const campaigns = rd.campaigns || [];
          const hasCampaigns = campaigns.length > 0;
          const rowKey = `${group.platform}::${row.name}`;
          const isExpanded = expandedRows?.has(rowKey);

          return (
            <React.Fragment key={row.name}>
              <tr
                className={`hover:bg-slate-50/80 transition-colors duration-100 ${hasCampaigns ? 'cursor-pointer' : ''}`}
                onClick={() => hasCampaigns && toggleRow(rowKey)}
              >
                <td className="sticky left-0 z-10 bg-white w-0 border-b border-slate-100" />
                <td className="sticky left-0 z-10 bg-white pl-7 pr-2 py-[6px] text-[11px] text-slate-600 border-b border-slate-100 whitespace-nowrap font-medium">
                  <div className="flex items-center gap-1.5">
                    {hasCampaigns && (
                      isExpanded
                        ? <ChevronDown size={10} className="text-slate-400 flex-shrink-0" />
                        : <ChevronRight size={10} className="text-slate-400 flex-shrink-0" />
                    )}
                    {row.name}
                    {hasCampaigns && (
                      <span className="text-[9px] text-slate-400 font-normal">({campaigns.length})</span>
                    )}
                  </div>
                </td>
                {COLUMNS.map(col => (
                  <td
                    key={col.key}
                    className={`px-2 py-[6px] text-right text-[10px] tabular-nums border-b border-slate-100 whitespace-nowrap ${
                      rd[col.key] != null ? 'text-slate-600' : 'text-slate-300'
                    }`}
                  >
                    {formatValue(rd[col.key], col.format)}
                  </td>
                ))}
              </tr>

              {isExpanded && campaigns.map((c, idx) => (
                <tr key={`${row.name}-c-${idx}`} className="bg-slate-50/60 hover:bg-blue-50/40 transition-colors duration-100">
                  <td className="sticky left-0 z-10 bg-slate-50/60 w-0 border-b border-slate-100/60" />
                  <td className="sticky left-0 z-10 bg-slate-50/60 pl-12 pr-2 py-[5px] text-[10px] text-slate-500 border-b border-slate-100/60 whitespace-nowrap truncate max-w-[280px]" title={c.name}>
                    {c.name}
                  </td>
                  {COLUMNS.map(col => (
                    <td
                      key={col.key}
                      className={`px-2 py-[5px] text-right text-[9px] tabular-nums border-b border-slate-100/60 whitespace-nowrap ${
                        c[col.key] != null && c[col.key] !== 0 ? 'text-slate-500' : 'text-slate-300'
                      }`}
                    >
                      {formatValue(c[col.key] || null, col.format)}
                    </td>
                  ))}
                </tr>
              ))}
            </React.Fragment>
          );
        })}
    </>
  );
}

function TotalRow({ data }) {
  const reachOf = useReachOf();
  const grand = useMemo(() => {
    const items = [];
    TABLE_DATA.forEach(g => {
      const pd = data?.[g.platform] || {};
      g.rows.forEach(r => items.push(pd[r.name]));
    });
    const t = sumMetrics(items, COLUMNS, reachOf);
    if (t.harcama > 0 && t.gelir != null) t.roas = t.gelir / t.harcama;
    return t;
  }, [data, reachOf]);

  return (
    <tr>
      <td
        colSpan={2}
        className="sticky left-0 z-10 pl-3 pr-2 py-[8px] text-[11px] font-bold text-white uppercase tracking-wider bg-slate-900 border-t border-slate-600"
      >
        Total
      </td>
      {COLUMNS.map(col => (
        <td
          key={col.key}
          className="px-2 py-[8px] text-right text-[10px] font-bold text-white bg-slate-900 tabular-nums border-t border-slate-600 whitespace-nowrap"
        >
          {formatValue(grand[col.key], col.format)}
        </td>
      ))}
    </tr>
  );
}

/* ── Özet tablo tanımı ──────────────────────────────────────────────── */

const SUMMARY_ROWS = [
  {
    label: 'Youtube',
    sources: [{ platform: 'Google Ads', rows: ['Youtube'] }],
  },
  {
    label: 'Google',
    sources: [{ platform: 'Google Ads', rows: ['Search', 'P-max Conv.', 'P-Max Branding', 'Demand Gen', 'Display / GDN', 'P-max Local'] }],
  },
  {
    label: 'Meta',
    sources: [{ platform: 'META', rows: ['Dönüşüm', 'Trafik', 'Bilinirlik', 'Etkileşim', 'Thruplay', 'Local - Store Visit'] }],
  },
  {
    label: 'Pinterest',
    sources: [{ platform: 'Pinterest', rows: ['Pinterest / Performans', 'Pinterest / Branding'] }],
  },
  {
    label: 'TikTok',
    sources: [{ platform: 'TikTok', rows: ['TikTok'] }],
  },
  {
    label: 'Remarketing',
    sources: [{ platform: 'Remarketing', rows: ['Criteo', 'RTB'] }],
  },
  {
    label: 'Medya Satın Alma',
    sources: [{ platform: 'Media Satın Alma', rows: ['Display', 'Native', 'Video', 'Programatik'] }],
  },
];

function SummaryTable({ data }) {
  const reachOf = useReachOf();
  const summaryData = useMemo(() => {
    const rows = SUMMARY_ROWS.map(sr => {
      const items = [];
      sr.sources.forEach(src => {
        const pd = data?.[src.platform] || {};
        src.rows.forEach(r => items.push(pd[r]));
      });
      const t = sumMetrics(items, COLUMNS, reachOf);
      if (t.harcama > 0 && t.gelir != null) t.roas = t.gelir / t.harcama;
      return { label: sr.label, metrics: t };
    });

    const total = sumMetrics(rows.map(r => r.metrics), COLUMNS, reachOf);
    if (total.harcama > 0 && total.gelir != null) total.roas = total.gelir / total.harcama;

    return { rows, total };
  }, [data, reachOf]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[1100px]">
          <thead>
            <tr>
              <th
                className="sticky left-0 z-20 bg-slate-700 px-3 py-2 text-left text-[10px] font-semibold text-slate-300 uppercase tracking-wider"
                style={{ minWidth: 220 }}
              >
                Mecra
              </th>
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  className="bg-slate-700 px-2 py-2 text-right text-[10px] font-semibold text-slate-300 uppercase tracking-wider whitespace-nowrap"
                  style={{ minWidth: 80 }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {summaryData.rows.map(row => (
              <tr key={row.label} className="hover:bg-slate-50/80 transition-colors duration-100">
                <td className="sticky left-0 z-10 bg-white px-3 py-[7px] text-[11px] text-slate-700 border-b border-slate-100 whitespace-nowrap font-semibold">
                  {row.label}
                </td>
                {COLUMNS.map(col => (
                  <td
                    key={col.key}
                    className={`px-2 py-[7px] text-right text-[10px] tabular-nums border-b border-slate-100 whitespace-nowrap ${
                      row.metrics[col.key] != null ? 'text-slate-600' : 'text-slate-300'
                    }`}
                  >
                    {formatValue(row.metrics[col.key], col.format)}
                  </td>
                ))}
              </tr>
            ))}
            <tr>
              <td className="sticky left-0 z-10 bg-slate-900 px-3 py-[8px] text-[11px] font-bold text-white uppercase tracking-wider border-t border-slate-600">
                Total
              </td>
              {COLUMNS.map(col => (
                <td
                  key={col.key}
                  className="px-2 py-[8px] text-right text-[10px] font-bold text-white bg-slate-900 tabular-nums border-t border-slate-600 whitespace-nowrap"
                >
                  {formatValue(summaryData.total[col.key], col.format)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Google Search Brand/Non-Brand tablosu ─────────────────────────── */

function GoogleSearchTable({ searchData, expandedRows, toggleRow }) {
  const SEARCH_ROWS = ['Pure Brand', 'Brand', 'Generic'];

  const total = useMemo(() => {
    const t = {};
    COLUMNS.forEach(col => {
      if (col.format === 'percent' || col.format === 'roas') return;
      let sum = 0, found = false;
      SEARCH_ROWS.forEach(r => {
        const v = searchData?.[r]?.[col.key];
        if (v != null) { sum += v; found = true; }
      });
      if (found) t[col.key] = sum;
    });
    if (t.harcama > 0 && t.gelir != null) t.roas = t.gelir / t.harcama;
    return t;
  }, [searchData]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[1100px]">
          <thead>
            <tr>
              <th
                className="sticky left-0 z-20 bg-slate-700 px-3 py-2 text-left text-[10px] font-semibold text-slate-300 uppercase tracking-wider"
                style={{ minWidth: 220 }}
              >
                Google Search
              </th>
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  className="bg-slate-700 px-2 py-2 text-right text-[10px] font-semibold text-slate-300 uppercase tracking-wider whitespace-nowrap"
                  style={{ minWidth: 80 }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SEARCH_ROWS.map(rowName => {
              const rd = searchData?.[rowName] || {};
              const campaigns = rd.campaigns || [];
              const hasCampaigns = campaigns.length > 0;
              const rowKey = `search::${rowName}`;
              const isExpanded = expandedRows?.has(rowKey);

              return (
                <React.Fragment key={rowName}>
                  <tr
                    className={`hover:bg-slate-50/80 transition-colors duration-100 ${hasCampaigns ? 'cursor-pointer' : ''}`}
                    onClick={() => hasCampaigns && toggleRow(rowKey)}
                  >
                    <td className="sticky left-0 z-10 bg-white px-3 py-[7px] text-[11px] text-slate-700 border-b border-slate-100 whitespace-nowrap font-semibold">
                      <div className="flex items-center gap-1.5">
                        {hasCampaigns && (
                          isExpanded
                            ? <ChevronDown size={10} className="text-slate-400 flex-shrink-0" />
                            : <ChevronRight size={10} className="text-slate-400 flex-shrink-0" />
                        )}
                        {rowName}
                        {hasCampaigns && (
                          <span className="text-[9px] text-slate-400 font-normal">({campaigns.length})</span>
                        )}
                      </div>
                    </td>
                    {COLUMNS.map(col => (
                      <td
                        key={col.key}
                        className={`px-2 py-[7px] text-right text-[10px] tabular-nums border-b border-slate-100 whitespace-nowrap ${
                          rd[col.key] != null && rd[col.key] !== 0 ? 'text-slate-600' : 'text-slate-300'
                        }`}
                      >
                        {formatValue(rd[col.key] || null, col.format)}
                      </td>
                    ))}
                  </tr>

                  {isExpanded && campaigns.map((c, idx) => (
                    <tr key={`${rowName}-c-${idx}`} className="bg-slate-50/60 hover:bg-blue-50/40 transition-colors duration-100">
                      <td className="sticky left-0 z-10 bg-slate-50/60 pl-8 pr-2 py-[5px] text-[10px] text-slate-500 border-b border-slate-100/60 whitespace-nowrap truncate max-w-[280px]" title={c.name}>
                        {c.name}
                      </td>
                      {COLUMNS.map(col => (
                        <td
                          key={col.key}
                          className={`px-2 py-[5px] text-right text-[9px] tabular-nums border-b border-slate-100/60 whitespace-nowrap ${
                            c[col.key] != null && c[col.key] !== 0 ? 'text-slate-500' : 'text-slate-300'
                          }`}
                        >
                          {formatValue(c[col.key] || null, col.format)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            <tr>
              <td className="sticky left-0 z-10 bg-slate-900 px-3 py-[8px] text-[11px] font-bold text-white uppercase tracking-wider border-t border-slate-600">
                TOTAL
              </td>
              {COLUMNS.map(col => (
                <td
                  key={col.key}
                  className="px-2 py-[8px] text-right text-[10px] font-bold text-white bg-slate-900 tabular-nums border-t border-slate-600 whitespace-nowrap"
                >
                  {formatValue(total[col.key], col.format)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Always On Generic tablosu ─────────────────────────────────────── */

function AlwaysOnTable({ aoData, expandedRows, toggleRow, tableTitle, tableId }) {
  const AO_ROWS = ['Google', 'Meta'];
  const reachOf = useReachOf();

  const total = useMemo(() => {
    const t = sumMetrics(AO_ROWS.map(r => aoData?.[r]), COLUMNS, reachOf);
    if (t.harcama > 0 && t.gelir != null) t.roas = t.gelir / t.harcama;
    return t;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aoData, reachOf]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[1100px]">
          <thead>
            <tr>
              <th
                className="sticky left-0 z-20 bg-slate-700 px-3 py-2 text-left text-[10px] font-semibold text-slate-300 uppercase tracking-wider"
                style={{ minWidth: 220 }}
              >
                {tableTitle || 'ALWAYS ON Generic'}
              </th>
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  className="bg-slate-700 px-2 py-2 text-right text-[10px] font-semibold text-slate-300 uppercase tracking-wider whitespace-nowrap"
                  style={{ minWidth: 80 }}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {AO_ROWS.map(rowName => {
              const rd = aoData?.[rowName] || {};
              const campaigns = rd.campaigns || [];
              const hasCampaigns = campaigns.length > 0;
              const rowKey = `${tableId || 'ao'}::${rowName}`;
              const isExpanded = expandedRows?.has(rowKey);

              return (
                <React.Fragment key={rowName}>
                  <tr
                    className={`hover:bg-slate-50/80 transition-colors duration-100 ${hasCampaigns ? 'cursor-pointer' : ''}`}
                    onClick={() => hasCampaigns && toggleRow(rowKey)}
                  >
                    <td className="sticky left-0 z-10 bg-white px-3 py-[7px] text-[11px] text-slate-700 border-b border-slate-100 whitespace-nowrap font-semibold">
                      <div className="flex items-center gap-1.5">
                        {hasCampaigns && (
                          isExpanded
                            ? <ChevronDown size={10} className="text-slate-400 flex-shrink-0" />
                            : <ChevronRight size={10} className="text-slate-400 flex-shrink-0" />
                        )}
                        {rowName}
                        {hasCampaigns && (
                          <span className="text-[9px] text-slate-400 font-normal">({campaigns.length})</span>
                        )}
                      </div>
                    </td>
                    {COLUMNS.map(col => (
                      <td
                        key={col.key}
                        className={`px-2 py-[7px] text-right text-[10px] tabular-nums border-b border-slate-100 whitespace-nowrap ${
                          rd[col.key] != null && rd[col.key] !== 0 ? 'text-slate-600' : 'text-slate-300'
                        }`}
                      >
                        {formatValue(rd[col.key] || null, col.format)}
                      </td>
                    ))}
                  </tr>

                  {isExpanded && campaigns.map((c, idx) => (
                    <tr key={`${rowName}-c-${idx}`} className="bg-slate-50/60 hover:bg-blue-50/40 transition-colors duration-100">
                      <td className="sticky left-0 z-10 bg-slate-50/60 pl-8 pr-2 py-[5px] text-[10px] text-slate-500 border-b border-slate-100/60 whitespace-nowrap truncate max-w-[280px]" title={c.name}>
                        {c.name}
                      </td>
                      {COLUMNS.map(col => (
                        <td
                          key={col.key}
                          className={`px-2 py-[5px] text-right text-[9px] tabular-nums border-b border-slate-100/60 whitespace-nowrap ${
                            c[col.key] != null && c[col.key] !== 0 ? 'text-slate-500' : 'text-slate-300'
                          }`}
                        >
                          {formatValue(c[col.key] || null, col.format)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
            <tr>
              <td className="sticky left-0 z-10 bg-slate-900 px-3 py-[8px] text-[11px] font-bold text-white uppercase tracking-wider border-t border-slate-600">
                TOTAL
              </td>
              {COLUMNS.map(col => (
                <td
                  key={col.key}
                  className="px-2 py-[8px] text-right text-[10px] font-bold text-white bg-slate-900 tabular-nums border-t border-slate-600 whitespace-nowrap"
                >
                  {formatValue(total[col.key], col.format)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ── Lansman tablosu (kampanya + ad group ismine göre filtrele) ────── */

function matchesKeyword(nameOrRow, kw, adGroupField) {
  const name = (nameOrRow?.name ?? nameOrRow?.campaignName ?? nameOrRow?.campaign_name ?? '').toString().toLowerCase();
  const adGroup = (adGroupField ?? nameOrRow?.adGroupName ?? nameOrRow?.ad_group_name ?? nameOrRow?.adset_name ?? '').toString().toLowerCase();
  const kwArr = Array.isArray(kw) ? kw : [kw];
  if (kwArr.some(k => name.includes(k) || (adGroup && adGroup.includes(k)))) return true;
  // Ad group isimleri (Google - array)
  const agNames = nameOrRow?.adGroupNames || [];
  if (agNames.length > 0 && agNames.some(ag => kwArr.some(k => ag.toLowerCase().includes(k)))) return true;
  // Adset isimleri (Meta - array)
  const asNames = nameOrRow?.adsetNames || [];
  if (asNames.length > 0 && asNames.some(as => kwArr.some(k => as.toLowerCase().includes(k)))) return true;
  // Ad isimleri (Meta - array)
  const adNames = nameOrRow?.adNames || [];
  if (adNames.length > 0 && adNames.some(ad => kwArr.some(k => ad.toLowerCase().includes(k)))) return true;
  return false;
}

/* Aranan kelimelerden herhangi biri metinde (substring) geçiyor mu? */
function kwHitAny(str, kwLower) {
  const v = String(str || '').toLowerCase();
  return !!v && kwLower.some(k => v.includes(k));
}

/*
 * Google (+ YouTube) granüler ladder toplama — Lansman/Kampanya filtresi.
 * Kural (kampanya bazında):
 *   1) KAMPANYA adı aranan kelimeyi içeriyorsa → tüm kampanya (kampanya-seviyesi
 *      total; trafik/GA4 oturum ve Pmax dahil, tam sadakat).
 *   2) yoksa yalnız adı eşleşen AD GROUP'ların toplamı (trafik granülde yok → 0).
 * Böylece "yatak" arandığında "Yataş" kampanyasının tümü değil, sadece
 * yatak ad group'unun costu yazılır.
 */
function aggregateGoogleLansman(campaignRows, adgroupRows, kwLower) {
  const mkBucket = () => ({ harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0, _camps: {} });
  const out = { Google: mkBucket(), YouTube: mkBucket() };
  const FIELDS = ['harcama', 'gosterim', 'tiklama', 'trafik', 'gelir', 'satis'];

  // Kampanya-seviyesi total (video kırılımı + trafik + Pmax burada tam)
  const campTotals = {};
  (campaignRows || []).forEach(row => {
    const cName = row.campaignName || '';
    const key = normName(cName);
    if (!key) return;
    const t = campTotals[key] || (campTotals[key] = {
      name: cName, type: (row.campaignType || '').toLowerCase(),
      harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0,
    });
    t.harcama  += Number(row.googleAdsCost) || 0;
    t.gosterim += Number(row.googleAdsImpression) || 0;
    t.tiklama  += Number(row.googleAdsClicks) || 0;
    t.trafik   += Number(row.sessions) || 0;
    t.gelir    += Number(row.googleAdsRevenue) || 0;
    t.satis    += Number(row.googleAdsTransaction) || 0;
  });

  // Ad group satırlarını kampanyaya göre grupla
  const agByCamp = {};
  (adgroupRows || []).forEach(row => {
    const key = normName(row.campaign_name);
    if (!key) return;
    (agByCamp[key] || (agByCamp[key] = [])).push(row);
  });

  const keys = new Set([...Object.keys(campTotals), ...Object.keys(agByCamp)]);
  keys.forEach(key => {
    const ct = campTotals[key];
    const ags = agByCamp[key] || [];
    const type = ct ? ct.type : String(ags[0]?.campaign_type || '').toLowerCase();
    const bucket = type.includes('video') ? out.YouTube : out.Google;
    const cName = ct ? ct.name : (ags[0]?.campaign_name || 'Unknown');

    let m = null;
    if (ct && kwHitAny(ct.name, kwLower)) {
      // 1) Kampanya adı eşleşti → tüm kampanya
      m = { harcama: ct.harcama, gosterim: ct.gosterim, tiklama: ct.tiklama, trafik: ct.trafik, gelir: ct.gelir, satis: ct.satis };
    } else {
      // 2) Yalnız adı eşleşen ad group'lar
      const matched = ags.filter(r => kwHitAny(r.ad_group_name, kwLower));
      if (matched.length) {
        m = { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 };
        matched.forEach(r => {
          m.harcama  += Number(r.googleAdsCost) || 0;
          m.gosterim += Number(r.googleAdsImpression) || 0;
          m.tiklama  += Number(r.googleAdsClicks) || 0;
          m.gelir    += Number(r.googleAdsRevenue) || 0;
          m.satis    += Number(r.googleAdsTransaction) || 0;
        });
      }
    }
    if (!m) return;
    FIELDS.forEach(f => { bucket[f] += m[f]; });
    const c = bucket._camps[cName] || (bucket._camps[cName] = { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 });
    FIELDS.forEach(f => { c[f] += m[f]; });
  });

  ['Google', 'YouTube'].forEach(k => {
    const b = out[k];
    b.campaigns = Object.entries(b._camps)
      .map(([name, mm]) => ({ name, ...mm, roas: mm.harcama > 0 ? mm.gelir / mm.harcama : 0 }))
      .sort((a, b2) => b2.harcama - a.harcama);
    delete b._camps;
  });
  return out;
}

/*
 * Meta granüler ladder toplama — Lansman/Kampanya filtresi.
 * Kural (kampanya bazında): kampanya adı eşleşirse tüm kampanya (kampanya-seviyesi
 * total; trafik/tekil kullanıcı dahil), yoksa en İNCE eşleşen alt seviye:
 * Ad adı → Adset adı. Yalnız eşleşen ad'ların toplamı yazılır.
 */
function aggregateMetaLansman(campaignRows, adRows, kwLower) {
  const out = { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0, _camps: {} };
  const CFIELDS = ['harcama', 'gosterim', 'etkilesim', 'erisim', 'izlenme', 'tiklama', 'tekilKullanici', 'trafik', 'gelir', 'satis'];

  // Kampanya-seviyesi total (unified: trafik + tekil kullanıcı burada var)
  const campTotals = {};
  (campaignRows || []).forEach(row => {
    const cName = row.campaign_name || row.campaignName || '';
    const key = normName(cName);
    if (!key) return;
    const t = campTotals[key] || (campTotals[key] = { name: cName, harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0, _mr: mrNew() });
    mrAddRow(t._mr, row, { level: 'campaign' });
    t.harcama  += Number(row.spend) || 0;
    t.gosterim += Number(row.impressions) || 0;
    t.etkilesim+= Number(row.actions) || 0;
    t.erisim   += Number(row.reach) || 0;
    t.izlenme  += Number(row.view || row.trueplay) || 0;
    t.tiklama  += Number(row.clicks) || 0;
    t.tekilKullanici += Number(row.ga4_users || row.ga4Users) || 0;
    t.trafik   += Number(row.ga4_sessions || row.ga4Sessions) || 0;
    t.gelir    += Number(row.conversion_value || row.conversionValue) || 0;
    t.satis    += Number(row.conversions) || 0;
  });

  // Ad satırlarını kampanyaya göre grupla
  const adsByCamp = {};
  (adRows || []).forEach(row => {
    const key = normName(row.campaign_name);
    if (!key) return;
    (adsByCamp[key] || (adsByCamp[key] = [])).push(row);
  });

  const sumAds = (rows) => {
    const m = { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0 };
    rows.forEach(r => {
      m.harcama  += Number(r.spend) || 0;
      m.gosterim += Number(r.impressions) || 0;
      m.etkilesim+= Number(r.actions) || 0;
      m.erisim   += Number(r.reach) || 0;
      m.izlenme  += Number(r.view || r.trueplay) || 0;
      m.tiklama  += Number(r.clicks) || 0;
      m.gelir    += Number(r.conversion_value) || 0;
      m.satis    += Number(r.conversions) || 0;
      // trafik / tekilKullanici ad seviyesinde yok → 0
    });
    return m;
  };

  const keys = new Set([...Object.keys(campTotals), ...Object.keys(adsByCamp)]);
  keys.forEach(key => {
    const ct = campTotals[key];
    const ads = adsByCamp[key] || [];
    const cName = ct ? ct.name : (ads[0]?.campaign_name || 'Unknown');

    let m = null;
    if (ct && kwHitAny(ct.name, kwLower)) {
      // 1) Kampanya adı → tüm kampanya
      m = { harcama: ct.harcama, gosterim: ct.gosterim, etkilesim: ct.etkilesim, erisim: ct.erisim, izlenme: ct.izlenme, tiklama: ct.tiklama, tekilKullanici: ct.tekilKullanici, trafik: ct.trafik, gelir: ct.gelir, satis: ct.satis };
      // Tüm kampanya: reklam satırları varsa reklam kümesi (lansman toplamı reklam
      // seçimleriyle tek Meta filtresinde birleşebilsin), yoksa kampanya kimliği
      m._mr = ads.length ? ads.reduce((sp, r) => mrAddRow(sp, r, { level: 'ad' }), mrNew()) : ct._mr;
    } else {
      // 2) Ad adı → 3) Adset adı (en ince eşleşen seviye)
      let matched = ads.filter(r => kwHitAny(r.ad_name, kwLower));
      if (!matched.length) matched = ads.filter(r => kwHitAny(r.adset_name, kwLower));
      if (matched.length) {
        m = sumAds(matched);
        m._mr = matched.reduce((sp, r) => mrAddRow(sp, r, { level: 'ad' }), mrNew());
      }
    }
    if (!m) return;
    CFIELDS.forEach(f => { out[f] += m[f]; });
    out._mr = mrUnion([out._mr, m._mr]);
    const c = out._camps[cName] || (out._camps[cName] = { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0, _mr: mrNew() });
    CFIELDS.forEach(f => { c[f] += m[f]; });
    c._mr = mrUnion([c._mr, m._mr]);
  });

  out.campaigns = Object.entries(out._camps)
    .map(([name, mm]) => ({ name, ...mm, roas: mm.harcama > 0 ? mm.gelir / mm.harcama : 0 }))
    .sort((a, b) => b.harcama - a.harcama);
  delete out._camps;
  return out;
}

function aggregateLansmanData(googleRaw, metaRaw, googleAdgroupsRaw, metaAdsRaw, reportData, keywords) {
  const kwList = Array.isArray(keywords) ? keywords : (keywords ? [keywords] : []);
  if (kwList.length === 0) return null;
  const kwLower = kwList.map(k => k.trim().toLowerCase()).filter(Boolean);
  if (kwLower.length === 0) return null;

  const result = {
    Google:        { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0, campaigns: [] },
    Meta:          { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, tekilKullanici: 0, trafik: 0, gelir: 0, satis: 0, campaigns: [] },
    YouTube:       { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0, campaigns: [] },
    Pinterest:     { harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, gelir: 0, satis: 0, campaigns: [] },
    TikTok:        { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0, campaigns: [] },
    Remarketing:   { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0, campaigns: [] },
    'Media Buying': { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0, izlenme: 0, campaigns: [] },
  };

  // Google + YouTube: granüler ad group ladder (yalnız adı eşleşen alt birim)
  const g = aggregateGoogleLansman(googleRaw, googleAdgroupsRaw, kwLower);
  result.Google = g.Google;
  result.YouTube = g.YouTube;

  // Meta: granüler ad → adset ladder
  result.Meta = aggregateMetaLansman(metaRaw, metaAdsRaw, kwLower);

  // Pinterest: reportData['Pinterest']['Pinterest / Performans'] and ['Pinterest / Branding']
  const pinData = reportData?.['Pinterest'] || {};
  if (Object.keys(pinData).length > 0) {
    const pinCamps = [];
    Object.values(pinData).forEach(bucket => {
      (bucket.campaigns || []).forEach(c => {
        if (!matchesKeyword(c, kwLower)) return;
        pinCamps.push(c);
        result.Pinterest.harcama += Number(c.harcama) || 0;
        result.Pinterest.gosterim += Number(c.gosterim) || 0;
        result.Pinterest.erisim += Number(c.erisim) || 0;
        result.Pinterest.izlenme += Number(c.izlenme) || 0;
        result.Pinterest.tiklama += Number(c.tiklama) || 0;
        result.Pinterest.gelir += Number(c.gelir) || 0;
        result.Pinterest.satis += Number(c.satis) || 0;
      });
    });
    result.Pinterest.campaigns = pinCamps.sort((a, b) => (b.harcama || 0) - (a.harcama || 0));
  }

  // TikTok: reportData['TikTok']['TikTok']
  const tiktokData = reportData?.['TikTok'] || {};
  if (Object.keys(tiktokData).length > 0) {
    const ttCamps = [];
    Object.values(tiktokData).forEach(bucket => {
      (bucket.campaigns || []).forEach(c => {
        if (!matchesKeyword(c, kwLower)) return;
        ttCamps.push(c);
        result.TikTok.harcama   += Number(c.harcama)   || 0;
        result.TikTok.gosterim  += Number(c.gosterim)  || 0;
        result.TikTok.etkilesim += Number(c.etkilesim) || 0;
        result.TikTok.erisim    += Number(c.erisim)    || 0;
        result.TikTok.izlenme   += Number(c.izlenme)   || 0;
        result.TikTok.tiklama   += Number(c.tiklama)   || 0;
        result.TikTok.trafik    += Number(c.trafik)    || 0;
        result.TikTok.gelir     += Number(c.gelir)     || 0;
        result.TikTok.satis     += Number(c.satis)     || 0;
      });
    });
    result.TikTok.campaigns = ttCamps.sort((a, b) => (b.harcama || 0) - (a.harcama || 0));
  }

  // Remarketing: Criteo + RTB
  const remData = reportData?.['Remarketing'] || {};
  ['Criteo', 'RTB'].forEach(source => {
    const bucket = remData[source];
    if (!bucket) return;
    (bucket.campaigns || []).forEach(c => {
      if (!matchesKeyword(c, kwLower)) return;
      result.Remarketing.harcama += Number(c.harcama) || 0;
      result.Remarketing.gosterim += Number(c.gosterim) || 0;
      result.Remarketing.tiklama += Number(c.tiklama) || 0;
      result.Remarketing.trafik += Number(c.trafik) || 0;
      result.Remarketing.gelir += Number(c.gelir) || 0;
      result.Remarketing.satis += Number(c.satis) || 0;
      result.Remarketing.campaigns.push(c);
    });
  });
  result.Remarketing.campaigns.sort((a, b) => (b.harcama || 0) - (a.harcama || 0));

  // Media Satın Alma: Display, Native, Video, Programatik
  const mediaData = reportData?.['Media Satın Alma'] || {};
  Object.values(mediaData).forEach(bucket => {
    (bucket.campaigns || []).forEach(c => {
      if (!matchesKeyword(c, kwLower)) return;
      result['Media Buying'].harcama += Number(c.harcama) || 0;
      result['Media Buying'].gosterim += Number(c.gosterim) || 0;
      result['Media Buying'].tiklama += Number(c.tiklama) || 0;
      result['Media Buying'].izlenme += Number(c.izlenme) || 0;
      result['Media Buying'].campaigns.push(c);
    });
  });
  result['Media Buying'].campaigns.sort((a, b) => (b.harcama || 0) - (a.harcama || 0));

  for (const key of Object.keys(result)) {
    const r = result[key];
    r.roas = r.harcama > 0 ? r.gelir / r.harcama : 0;
    if (r.campaigns) {
      r.campaigns.forEach(c => { c.roas = c.harcama > 0 ? (c.gelir || 0) / c.harcama : 0; });
    }
  }

  return result;
}

function LansmanTable({ id, title, rows, googleRaw, metaRaw, googleAdgroupsRaw, metaAdsRaw, reportData, expandedRows, toggleRow, quickTags = [], tags, onTagsChange }) {
  const [inputValue, setInputValue] = useState('');
  const setTags = onTagsChange;
  const tagsRef = useRef(tags);
  tagsRef.current = tags;

  const reachOf = useReachOf();
  const lansmanRaw = useMemo(
    () => aggregateLansmanData(googleRaw, metaRaw, googleAdgroupsRaw, metaAdsRaw, reportData, tags),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [googleRaw, metaRaw, googleAdgroupsRaw, metaAdsRaw, reportData, JSON.stringify(tags)]
  );
  const lansmanData = useMemo(() => withDedupReach(lansmanRaw, reachOf), [lansmanRaw, reachOf]);

  const LANSMAN_ROWS = rows || ['Google', 'Meta', 'YouTube', 'Pinterest', 'Remarketing', 'Media Buying'];

  const total = useMemo(() => {
    if (!lansmanData) return {};
    const t = sumMetrics(LANSMAN_ROWS.map(r => lansmanData[r]), COLUMNS, reachOf);
    if (t.harcama > 0 && t.gelir != null) t.roas = t.gelir / t.harcama;
    return t;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lansmanData, reachOf]);

  const addTag = useCallback((val) => {
    const v = (val || '').trim();
    if (!v) return;
    setTags(prev => {
      if (prev.includes(v)) return prev;
      return [...prev, v];
    });
  }, [setTags]);

  const removeTag = useCallback((idx) => {
    setTags(prev => prev.filter((_, i) => i !== idx));
  }, [setTags]);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <div className="relative flex-1 max-w-lg flex items-center flex-wrap gap-1.5 border border-slate-200 rounded-md bg-white px-2 py-1.5 focus-within:ring-2 focus-within:ring-blue-500/30 focus-within:border-blue-400 shadow-sm min-h-[32px]">
          <Search size={13} className="text-slate-400 flex-shrink-0" />
          {tags.map((tag, idx) => (
            <span
              key={idx}
              className="inline-flex items-center gap-1 bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full text-[10px] font-medium"
            >
              {tag}
              <button
                onClick={() => removeTag(idx)}
                className="hover:text-red-500 transition-colors font-bold text-[10px] leading-none"
              >
                ×
              </button>
            </span>
          ))}
          <input
            type="text"
            value={inputValue}
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const v = e.target.value.trim();
                if (v) {
                  addTag(v);
                  setInputValue('');
                }
              }
              if (e.key === 'Backspace' && !e.target.value && tagsRef.current.length > 0) {
                removeTag(tagsRef.current.length - 1);
              }
            }}
            placeholder={tags.length === 0 ? 'Kampanya adı yaz, Enter ile ekle… (OR mantığı)' : 'Başka ekle…'}
            className="flex-1 min-w-[120px] text-[11px] bg-transparent text-slate-700 placeholder-slate-400 focus:outline-none border-none p-0"
          />
        </div>
        {tags.length > 0 && (
          <button
            onClick={() => { setTags([]); setInputValue(''); }}
            className="text-[10px] text-slate-500 hover:text-red-500 transition-colors whitespace-nowrap"
          >
            Temizle
          </button>
        )}
      </div>

      {quickTags.length > 0 && (
        <div className="flex items-center gap-1.5 mb-2">
          <span className="text-[10px] text-slate-400">Hızlı filtre:</span>
          {quickTags.map(qt => {
            const isActive = tags.includes(qt);
            return (
              <button
                key={qt}
                onClick={() => {
                  if (isActive) { setTags(prev => prev.filter(t => t !== qt)); }
                  else { addTag(qt); }
                }}
                className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors border ${
                  isActive
                    ? 'bg-blue-100 text-blue-700 border-blue-300'
                    : 'bg-slate-50 text-slate-500 border-slate-200 hover:bg-slate-100'
                }`}
              >
                {qt}
              </button>
            );
          })}
        </div>
      )}

      {tags.length > 0 && (
        <div className="mb-2 text-[10px] text-slate-500">
          Filtre: {tags.map((t, i) => <span key={i}>{i > 0 && <span className="text-slate-400 mx-1">veya</span>}<span className="font-semibold text-slate-700">"{t}"</span></span>)}
          {lansmanData && ` — ${LANSMAN_ROWS.reduce((n, r) => n + (lansmanData[r]?.campaigns?.length || 0), 0)} kampanya bulundu`}
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[1100px]">
            <thead>
              <tr>
                <th
                  className="sticky left-0 z-20 bg-slate-700 px-3 py-2 text-left text-[10px] font-semibold text-slate-300 uppercase tracking-wider"
                  style={{ minWidth: 220 }}
                >
                  {title || 'LANSMAN'}
                </th>
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    className="bg-slate-700 px-2 py-2 text-right text-[10px] font-semibold text-slate-300 uppercase tracking-wider whitespace-nowrap"
                    style={{ minWidth: 80 }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {LANSMAN_ROWS.map(rowName => {
                const rd = lansmanData?.[rowName] || {};
                const campaigns = rd.campaigns || [];
                const hasCampaigns = campaigns.length > 0;
                const rowKey = `${id || 'lansman'}::${rowName}`;
                const isExpanded = expandedRows?.has(rowKey);

                return (
                  <React.Fragment key={rowName}>
                    <tr
                      className={`hover:bg-slate-50/80 transition-colors duration-100 ${hasCampaigns ? 'cursor-pointer' : ''}`}
                      onClick={() => hasCampaigns && toggleRow(rowKey)}
                    >
                      <td className="sticky left-0 z-10 bg-white px-3 py-[7px] text-[11px] text-slate-700 border-b border-slate-100 whitespace-nowrap font-semibold">
                        <div className="flex items-center gap-1.5">
                          {hasCampaigns && (
                            isExpanded
                              ? <ChevronDown size={10} className="text-slate-400 flex-shrink-0" />
                              : <ChevronRight size={10} className="text-slate-400 flex-shrink-0" />
                          )}
                          {rowName}
                          {hasCampaigns && (
                            <span className="text-[9px] text-slate-400 font-normal">({campaigns.length})</span>
                          )}
                        </div>
                      </td>
                      {COLUMNS.map(col => (
                        <td
                          key={col.key}
                          className={`px-2 py-[7px] text-right text-[10px] tabular-nums border-b border-slate-100 whitespace-nowrap ${
                            rd[col.key] != null && rd[col.key] !== 0 ? 'text-slate-600' : 'text-slate-300'
                          }`}
                        >
                          {formatValue(rd[col.key] || null, col.format)}
                        </td>
                      ))}
                    </tr>

                    {isExpanded && campaigns.map((c, idx) => (
                      <tr key={`${rowName}-c-${idx}`} className="bg-slate-50/60 hover:bg-blue-50/40 transition-colors duration-100">
                        <td className="sticky left-0 z-10 bg-slate-50/60 pl-8 pr-2 py-[5px] text-[10px] text-slate-500 border-b border-slate-100/60 whitespace-nowrap truncate max-w-[280px]" title={c.name}>
                          {c.name}
                        </td>
                        {COLUMNS.map(col => (
                          <td
                            key={col.key}
                            className={`px-2 py-[5px] text-right text-[9px] tabular-nums border-b border-slate-100/60 whitespace-nowrap ${
                              c[col.key] != null && c[col.key] !== 0 ? 'text-slate-500' : 'text-slate-300'
                            }`}
                          >
                            {formatValue(c[col.key] || null, col.format)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </React.Fragment>
                );
              })}
              <tr>
                <td className="sticky left-0 z-10 bg-slate-900 px-3 py-[8px] text-[11px] font-bold text-white uppercase tracking-wider border-t border-slate-600">
                  TOTAL
                </td>
                {COLUMNS.map(col => (
                  <td
                    key={col.key}
                    className="px-2 py-[8px] text-right text-[10px] font-bold text-white bg-slate-900 tabular-nums border-t border-slate-600 whitespace-nowrap"
                  >
                    {formatValue(total[col.key], col.format)}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ── Ana bileşen ────────────────────────────────────────────────────── */

export default function CustomReport1() {
  const [startDate, setStartDate] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [endDate, setEndDate] = useState(new Date());
  const [collapsedPlatforms, setCollapsedPlatforms] = useState(new Set());
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [reportDataRaw, setReportData] = useState({});
  const [searchParams, setSearchParams] = useSearchParams();
  const scope = scopeFromParam(searchParams.get('butce'));
  const setScope = (k) => {
    const next = new URLSearchParams(searchParams);
    if (SCOPE_PARAM[k]) next.set('butce', SCOPE_PARAM[k]); else next.delete('butce');
    setSearchParams(next, { replace: true });
  };
  const inScope = useCallback((p) => {
    if (scope === 'all') return true;
    const brandId = localStorage.getItem('currentBrandId');
    return classifyCampaign({ ...p, brandId }) === scope;
  }, [scope]);
  // Meta erişimi: DB toplamı yerine Meta API'den tekil değer (utils/metaReachDedup)
  const reachOf = useMetaReach('https://api.glowytics.com', localStorage.getItem('currentBrandId'), startDate, endDate);
  const reportData = useMemo(() => withDedupReach(reportDataRaw, reachOf), [reportDataRaw, reachOf]);
  const [loading, setLoading] = useState(false);
  const [campaignTags, setCampaignTags] = useState({ ana: [], kamp1: [], kamp2: [], kamp3: [] });
  const rawDataRef = useRef({ google: null, meta: null, googleAdgroups: null, metaAds: null });
  const pdfRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  const handleExportPDF = useCallback(async () => {
    if (exporting || !pdfRef.current) return;
    setExporting(true);
    try {
      const el = pdfRef.current;
      const canvas = await html2canvas(el, { scale: 1.5, useCORS: true, logging: false, backgroundColor: '#ffffff', windowWidth: 1400, letterRendering: true, allowTaint: true });
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
      const pageW = pdf.internal.pageSize.getWidth();
      const pageH = pdf.internal.pageSize.getHeight();
      const margin = 8;
      const usableW = pageW - margin * 2;
      const usableH = pageH - margin * 2;
      const imgW = usableW;
      const imgH = (canvas.height * usableW) / canvas.width;
      const imgData = canvas.toDataURL('image/jpeg', 0.92);
      let y = 0;
      let page = 0;
      while (y < imgH) {
        if (page > 0) pdf.addPage();
        const srcY = (y / imgH) * canvas.height;
        const srcH = Math.min((usableH / imgH) * canvas.height, canvas.height - srcY);
        const sliceCanvas = document.createElement('canvas');
        sliceCanvas.width = canvas.width;
        sliceCanvas.height = srcH;
        sliceCanvas.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
        const sliceData = sliceCanvas.toDataURL('image/jpeg', 0.92);
        const sliceH = (srcH * usableW) / canvas.width;
        pdf.addImage(sliceData, 'JPEG', margin, margin, imgW, sliceH);
        y += usableH;
        page++;
      }
      pdf.save(scope === 'all' ? 'Kampanya_Performans_Raporu.pdf' : `Kampanya_Performans_Raporu_${SCOPE_PARAM[scope]}.pdf`);
    } catch (err) { console.error('PDF export failed:', err); }
    finally { setExporting(false); }
  }, [exporting, scope]);

  /* ── Google Ads veri çekme ──────────────────────────────────────────── */

  const fetchGoogleAdsData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(startDate),
        endDate: fmtDate(endDate),
      });

      const res = await fetch(
        `https://api.glowytics.com/api/google-ads/campaign-types-all?${params}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) throw new Error('Google Ads verisi alınamadı');
      const json = await res.json();
      const googleRaw = (json.rawData || []).filter(r =>
        inScope({ name: r.campaignName, platform: 'google', campaignType: r.campaignType }));
      rawDataRef.current.google = googleRaw;
      const googleAdsAggregated = aggregateGoogleAdsData(googleRaw);
      const searchAggregated = aggregateGoogleSearchData(googleRaw);

      setReportData(prev => ({
        ...prev,
        'Google Ads': googleAdsAggregated,
        '_googleSearch': searchAggregated,
      }));
    } catch (err) {
      console.error('Google Ads fetch error:', err);
    }
  }, [startDate, endDate, inScope]);

  /* ── Google Ads Video veri çekme (izlenme + VCR) ─────────────────── */

  const fetchGoogleAdsVideoData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(startDate),
        endDate: fmtDate(endDate),
      });

      const res = await fetch(
        `https://api.glowytics.com/api/google-ads/video-performance-summary?${params}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) throw new Error('Google Ads Video verisi alınamadı');
      const rows = await res.json();
      const arr = (Array.isArray(rows) ? rows : rows.data || rows.rawData || []).filter(r =>
        inScope({ name: r.campaign_name, platform: 'google', campaignType: r.campaign_type || 'VIDEO' }));

      // İzlenmeyi kampanya TÜRÜNE göre satırlara dağıt.
      // Eskiden gelen her satır tek bir 'Youtube' kovasına yazılıyordu; PMax /
      // Demand Gen / Display izlenmeleri ingestion'a eklendikten sonra bu, PMax
      // izlenmesinin YouTube etiketiyle görünmesine yol açtı. Artık zaten var olan
      // classifyGoogleAdsCampaign() ile 'P-max Conv.' / 'P-Max Branding' /
      // 'P-max Local' / 'Demand Gen' / 'Display / GDN' / 'Youtube' satırlarına ayrılıyor.
      const byRow = {};   // satır adı -> { views, impressions }
      arr.forEach(r => {
        // Bumper/non-skippable/CTV (view=0) → impression'ı izlenme say (backend effective_views).
        const vv = Number(r.video_views) || 0;
        const ev = r.effective_views != null ? Number(r.effective_views) : (vv > 0 ? vv : Number(r.impressions) || 0);
        if (!ev) return;
        // campaign_type dönmeyen eski cevaplarda davranış aynı kalsın → Youtube.
        const rowName = classifyGoogleAdsCampaign(r.campaign_type || 'VIDEO', r.campaign_name) || 'Youtube';
        const b = byRow[rowName] || (byRow[rowName] = { views: 0, impressions: 0 });
        b.views += ev;
        b.impressions += Number(r.impressions) || 0;
      });

      if (Object.keys(byRow).length) {
        setReportData(prev => {
          const ga = { ...(prev['Google Ads'] || {}) };
          Object.entries(byRow).forEach(([rowName, b]) => {
            ga[rowName] = {
              ...(ga[rowName] || {}),
              izlenme: b.views,
              vcr: b.impressions > 0 ? b.views / b.impressions : 0,
            };
          });
          return { ...prev, 'Google Ads': ga };
        });
      }
    } catch (err) {
      console.error('Google Ads Video fetch error:', err);
    }
  }, [startDate, endDate, inScope]);

  /* ── Meta Ads veri çekme ──────────────────────────────────────────── */

  const fetchMetaData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(startDate),
        endDate: fmtDate(endDate),
      });

      const res = await fetch(
        `https://api.glowytics.com/api/google-ads/unified-meta-ga4-performance?${params}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) throw new Error('Meta Ads verisi alınamadı');
      const json = await res.json();
      // Yunanistan gibi `raporlardanCikar` kampanyaları hiçbir raporda görünmez (Yataş, 6 Eki 2026).
      const metaRaw = haricleriAyikla(Array.isArray(json) ? json : json.data || json.rawData || [], brandId)
        .filter(r => inScope({
          name: r.campaign_name || r.campaignName, platform: 'meta',
          objective: r.campaign_objective || r.campaignObjective,
        }));
      rawDataRef.current.meta = metaRaw;
      const metaAggregated = aggregateMetaData(metaRaw);

      setReportData(prev => ({
        ...prev,
        'META': metaAggregated,
      }));
    } catch (err) {
      console.error('Meta Ads fetch error:', err);
    }
  }, [startDate, endDate, inScope]);

  /* ── Google Ads adgroup-level veri çekme (Always On filtresi için) ── */

  const fetchGoogleAdgroups = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(startDate),
        endDate: fmtDate(endDate),
      });

      const res = await fetch(
        `https://api.glowytics.com/api/google-ads/adgroup-performance?${params}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) throw new Error('Google Ads adgroup verisi alınamadı');
      // Endpoint returns a bare array (not wrapped in an object)
      const json = await res.json();
      rawDataRef.current.googleAdgroups = (Array.isArray(json) ? json : (json.rawData || json.data || []))
        .filter(r => inScope({ name: r.campaign_name, platform: 'google', campaignType: r.campaign_type }));
    } catch (err) {
      console.error('Google Ads adgroup fetch error:', err);
      rawDataRef.current.googleAdgroups = [];
    }
  }, [startDate, endDate, inScope]);

  /* ── Meta Ads ad-level veri çekme (Always On filtresi için) ─────── */

  const fetchMetaAds = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({
        dateRange: 'custom',
        startDate: fmtDate(startDate),
        endDate: fmtDate(endDate),
      });

      const res = await fetch(
        `https://api.glowytics.com/api/meta-ads/ad-performance?${params}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) throw new Error('Meta Ads ad verisi alınamadı');
      // Endpoint returns a bare array (not wrapped in an object)
      const json = await res.json();
      rawDataRef.current.metaAds = haricleriAyikla(Array.isArray(json) ? json : (json.rawData || json.data || []), brandId)
        .filter(r => inScope({ name: r.campaign_name, platform: 'meta', objective: r.campaign_objective }));
    } catch (err) {
      console.error('Meta Ads ad-level fetch error:', err);
      rawDataRef.current.metaAds = [];
    }
  }, [startDate, endDate, inScope]);

  /* ── Criteo veri çekme ─────────────────────────────────────────────── */

  const fetchCriteoData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({
        startDate: fmtDate(startDate),
        endDate: fmtDate(endDate),
      });

      const res = await fetch(
        `https://api.glowytics.com/api/criteo/db/campaign-summary?${params}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) return;
      const json = await res.json();
      if (!json.success) return;

      // Criteo da retargeting: kural RTB House ile aynı (adında sinyal yoksa performans).
      const campaigns = (json.campaigns || []).map(c => ({
        name: c.campaign_name || c.campaign_id || 'Unknown',
        harcama: c.cost || 0,
        gosterim: c.impressions || 0,
        tiklama: c.clicks || 0,
      })).filter(c => inScope({ name: c.name, platform: 'rtbhouse' }));

      const totals = scope === 'all' ? {
        harcama: json.totals?.cost || 0,
        gosterim: json.totals?.impressions || 0,
        tiklama: json.totals?.clicks || 0,
        campaigns,
      } : {
        harcama: campaigns.reduce((t, c) => t + c.harcama, 0),
        gosterim: campaigns.reduce((t, c) => t + c.gosterim, 0),
        tiklama: campaigns.reduce((t, c) => t + c.tiklama, 0),
        campaigns,
      };

      setReportData(prev => ({
        ...prev,
        'Remarketing': {
          ...(prev['Remarketing'] || {}),
          'Criteo': totals,
        },
      }));
    } catch (err) {
      console.error('Criteo fetch error:', err);
    }
  }, [startDate, endDate, inScope, scope]);

  /* ── RTB House veri çekme ────────────────────────────────────────── */

  const fetchRtbHouseData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({
        dayFrom: fmtDate(startDate),
        dayTo: fmtDate(endDate),
      });

      const res = await fetch(
        `https://api.glowytics.com/api/rtbhouse/stats/by-campaign?${params}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) return;
      const json = await res.json();
      if (!json.success) return;

      const campaigns = (json.rows || []).map(r => ({
        name: r.campaignName || r.subcampaign || 'Unknown',
        adGroupName: r.subcampaign || null,
        harcama: r.campaignCost || 0,
        gosterim: r.impsCount || 0,
        tiklama: r.clicksCount || 0,
        trafik: r.ga4Sessions || 0,
        gelir: r.ga4Revenue || 0,
        satis: r.ga4Purchases || 0,
      })).filter(c => inScope({ name: c.name, platform: 'rtbhouse' }));

      const totals = campaigns.reduce((t, c) => ({
        harcama: t.harcama + c.harcama,
        gosterim: t.gosterim + c.gosterim,
        tiklama: t.tiklama + c.tiklama,
        trafik: t.trafik + c.trafik,
        gelir: t.gelir + c.gelir,
        satis: t.satis + c.satis,
      }), { harcama: 0, gosterim: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 });

      setReportData(prev => ({
        ...prev,
        'Remarketing': {
          ...(prev['Remarketing'] || {}),
          'RTB': { ...totals, campaigns },
        },
      }));
    } catch (err) {
      console.error('RTB House fetch error:', err);
    }
  }, [startDate, endDate, inScope]);

  /* ── Pinterest veri çekme ─────────────────────────────────────────── */

  const fetchPinterestData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({
        startDate: fmtDate(startDate),
        endDate: fmtDate(endDate),
      });

      const res = await fetch(
        `https://api.glowytics.com/api/pinterest/db/stats?${params}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) return;
      const json = await res.json();

      const buckets = {
        'Pinterest / Performans': { harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, gelir: 0, satis: 0, campaigns: [] },
        'Pinterest / Branding': { harcama: 0, gosterim: 0, erisim: 0, izlenme: 0, tiklama: 0, gelir: 0, satis: 0, campaigns: [] },
      };

      (json.campaigns || []).forEach(c => {
        if (!inScope({ name: c.name, platform: 'pinterest' })) return;
        const name = (c.name || '').toLowerCase();
        const bucket = (name.includes('brand') || name.includes('awareness') || name.includes('reach') || name.includes('traffic'))
          ? 'Pinterest / Branding'
          : 'Pinterest / Performans';

        const mapped = {
          name: c.name || c.id || 'Unknown',
          harcama: Number(c.spend) || 0,
          gosterim: Number(c.impressions) || 0,
          erisim: Number(c.reach) || 0,
          izlenme: Number(c.video_views) || 0,
          tiklama: Number(c.clicks) || 0,
          gelir: Number(c.conversions_value) || 0,
          satis: Number(c.conversions) || 0,
        };

        buckets[bucket].harcama += mapped.harcama;
        buckets[bucket].gosterim += mapped.gosterim;
        buckets[bucket].erisim += mapped.erisim;
        buckets[bucket].izlenme += mapped.izlenme;
        buckets[bucket].tiklama += mapped.tiklama;
        buckets[bucket].gelir += mapped.gelir;
        buckets[bucket].satis += mapped.satis;
        buckets[bucket].campaigns.push(mapped);
      });

      Object.values(buckets).forEach(b => b.campaigns.sort((a, b) => b.harcama - a.harcama));

      const result = {};
      Object.entries(buckets).forEach(([key, val]) => {
        if (val.campaigns.length > 0 || val.harcama > 0) {
          result[key] = val;
        }
      });

      setReportData(prev => ({
        ...prev,
        'Pinterest': result,
      }));
    } catch (err) {
      console.error('Pinterest fetch error:', err);
    }
  }, [startDate, endDate, inScope]);

  /* ── TikTok veri çekme ───────────────────────────────────────────── */

  const fetchTikTokData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      const params = new URLSearchParams({
        startDate: fmtDate(startDate),
        endDate: fmtDate(endDate),
      });

      const res = await fetch(
        `https://api.glowytics.com/api/tiktok-ads/unified-campaign-performance?${params}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) return;
      const rawRows = await res.json();
      if (!Array.isArray(rawRows)) return;
      const rows = rawRows.filter(r =>
        inScope({ name: r.campaign_name, platform: 'tiktok', objective: r.campaign_objective }));

      const totals = rows.reduce((t, r) => {
        t.harcama   += Number(r.spend)                || 0;
        t.gosterim  += Number(r.impressions)          || 0;
        t.etkilesim += (Number(r.likes) || 0) + (Number(r.comments) || 0) + (Number(r.shares) || 0);
        t.erisim    += Number(r.reach)                || 0;
        // TikTok Ads Manager'daki "Video İzlenme" = video_play_actions.
        // (video_views_p100 %100 tamamlamadır, ~160× düşük çıkıyordu.)
        t.izlenme   += Number(r.video_play_actions)   || 0;
        t.tiklama   += Number(r.clicks)               || 0;
        t.trafik    += Number(r.ga4_sessions)         || 0;
        t.gelir     += Number(r.ga4_total_revenue)    || 0;
        t.satis     += Number(r.ga4_purchases)        || 0;
        return t;
      }, { harcama: 0, gosterim: 0, etkilesim: 0, erisim: 0, izlenme: 0, tiklama: 0, trafik: 0, gelir: 0, satis: 0 });

      totals.campaigns = [];

      setReportData(prev => ({
        ...prev,
        'TikTok': {
          ...(prev['TikTok'] || {}),
          'TikTok': totals,
        },
      }));
    } catch (err) {
      console.error('TikTok fetch error:', err);
    }
  }, [startDate, endDate, inScope]);

  /* ── Medya Satın Alma veri çekme (DB) ────────────────────────────── */

  const fetchMediaBuyingData = useCallback(async () => {
    try {
      const brandId = localStorage.getItem('currentBrandId');
      if (!brandId) return;

      // Medya satın alma tamamen branding bütçesi (report-3 ile aynı) → performans kapsamında yok.
      if (scope === 'performance') {
        setReportData(prev => ({ ...prev, 'Media Satın Alma': {} }));
        return;
      }

      const year = endDate.getFullYear();
      const month = endDate.getMonth() + 1;

      const res = await fetch(
        `https://api.glowytics.com/api/brands/${brandId}/media-buying-format?year=${year}&month=${month}`,
        {
          credentials: 'include',
          headers: { 'X-Brand-ID': brandId, 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        }
      );

      if (!res.ok) return;
      const json = await res.json();
      // O ay medya satın alma girilmemişse reportData'yı temizle ki üst tabloda grup
      // gizlenebilsin ve önceki aydan bayat veri kalmasın.
      if (!json.success || !Array.isArray(json.sections)) {
        setReportData(prev => ({ ...prev, 'Media Satın Alma': {} }));
        return;
      }

      // Bazı aylarda sadece "Mecraları" tablosu giriliyor (Dağılım yok) → eskiden boş
      // dönüyordu. Kanonik kural (report-2/3 + backend normalizer ile aynı): Dağılım
      // varsa onu kullan, yoksa Mecra'ya düş (toplam aynı para, kırılım yayıncıya iner).
      const isMecra = (t) => (t || '').toLowerCase().includes('mecra');
      const mbSection =
        json.sections.find(s => s.title === 'Medya Satın Alma Dağılımı') ||
        json.sections.find(s => isMecra(s.title)) ||
        json.sections.find(s => Array.isArray(s.rows) && s.rows.length) ||
        json.sections[0];
      const distSection = mbSection;
      if (!distSection || !Array.isArray(distSection.rows)) {
        setReportData(prev => ({ ...prev, 'Media Satın Alma': {} }));
        return;
      }

      const parseTR = (str) => {
        if (!str || str === '-' || str === '–') return 0;
        const cleaned = String(str).replace(/[₺\s]/g, '').replace(/\./g, '').replace(/,/g, '.');
        const num = parseFloat(cleaned);
        return isNaN(num) ? 0 : num;
      };

      const channelBuckets = {};
      distSection.rows.forEach(r => {
        const channel = (r.channel || '').trim();
        if (!channel) return;

        if (!channelBuckets[channel]) {
          channelBuckets[channel] = { harcama: 0, gosterim: 0, tiklama: 0, izlenme: 0, campaigns: [] };
        }

        const harcama = parseTR(r.budget);
        const gosterim = parseTR(r.impressions);
        const tiklama = parseTR(r.clicks);
        const izlenme = parseTR(r.views);

        channelBuckets[channel].harcama += harcama;
        channelBuckets[channel].gosterim += gosterim;
        channelBuckets[channel].tiklama += tiklama;
        channelBuckets[channel].izlenme += izlenme;
        channelBuckets[channel].campaigns.push({
          name: r.campaign || channel,
          harcama, gosterim, tiklama, izlenme,
        });
      });

      Object.values(channelBuckets).forEach(b => b.campaigns.sort((a, b) => b.harcama - a.harcama));

      setReportData(prev => ({
        ...prev,
        'Media Satın Alma': channelBuckets,
      }));
    } catch (err) {
      console.error('Media Buying fetch error:', err);
    }
  }, [endDate, scope]);

  /* ── Tüm verileri yükle ─────────────────────────────────────────────── */

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    rawDataRef.current = { google: null, meta: null, googleAdgroups: null, metaAds: null };
    await Promise.all([
      fetchGoogleAdsData(),
      fetchMetaData(),
      // Granular adgroup/ad fetches for Always On keyword filtering
      fetchGoogleAdgroups(),
      fetchMetaAds(),
      fetchCriteoData(),
      fetchRtbHouseData(),
      fetchPinterestData(),
      fetchTikTokData(),
      fetchMediaBuyingData(),
    ]);
    await fetchGoogleAdsVideoData();

    // aggregateByKeyword now uses granular adgroup/ad rows so the match is
    // scoped to only the adgroups/ads whose name contains the keyword —
    // not the entire parent campaign's aggregated metrics.
    const aoData = aggregateByKeyword(
      rawDataRef.current.googleAdgroups,
      rawDataRef.current.metaAds,
      ['AO', 'Always On', 'Always']
    );
    const localData = aggregateByKeyword(
      rawDataRef.current.googleAdgroups,
      rawDataRef.current.metaAds,
      ['LOCAL STORE', 'STORE VISIT', 'LOCAL VISIT', 'LOKAL', 'MAĞAZA']
    );
    setReportData(prev => ({ ...prev, '_alwaysOn': aoData, '_localStore': localData }));

    setLoading(false);
  }, [fetchGoogleAdsData, fetchGoogleAdsVideoData, fetchMetaData, fetchGoogleAdgroups, fetchMetaAds, fetchCriteoData, fetchRtbHouseData, fetchPinterestData, fetchTikTokData, fetchMediaBuyingData]);

  useEffect(() => {
    fetchAllData();
  }, [fetchAllData]);

  /* ── UI aksiyonları ─────────────────────────────────────────────────── */

  const togglePlatform = (p) => {
    setCollapsedPlatforms(prev => {
      const n = new Set(prev);
      n.has(p) ? n.delete(p) : n.add(p);
      return n;
    });
  };

  const toggleRow = (rowKey) => {
    setExpandedRows(prev => {
      const n = new Set(prev);
      n.has(rowKey) ? n.delete(rowKey) : n.add(rowKey);
      return n;
    });
  };

  const expandAll = () => setCollapsedPlatforms(new Set());
  const collapseAll = () => {
    setCollapsedPlatforms(new Set(TABLE_DATA.map(g => g.platform)));
    setExpandedRows(new Set());
  };

  return (
    <MetaReachContext.Provider value={reachOf}>
    <div className="min-h-screen" ref={pdfRef}>
      {/* Header */}
      <div className="mb-4 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="text-lg font-bold text-slate-800 tracking-tight">
            Kampanya Performans Raporu
          </h1>
          <p className="text-[11px] text-slate-400 mt-0.5">
            {scope === 'branding'
              ? 'Yalnız branding bütçesinden yapılan harcamalar'
              : scope === 'performance'
                ? 'Yalnız performans bütçesinden yapılan harcamalar'
                : 'Tüm reklam mecralarının detaylı kampanya performans özeti'}
          </p>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex items-center bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden text-[11px]" title="Bütçe kapsamı">
            {SCOPES.map((o, i) => (
              <button
                key={o.key}
                onClick={() => setScope(o.key)}
                disabled={loading}
                className={`px-2.5 py-1 font-medium transition-colors ${i > 0 ? 'border-l border-slate-200' : ''} ${
                  scope === o.key ? 'bg-slate-700 text-white' : 'text-slate-500 hover:bg-slate-50'
                }`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <ConsistencyBadge startDate={startDate} endDate={endDate} />
          {[
            { label: 'Bu Ay', fn: () => { const n = new Date(); setStartDate(new Date(n.getFullYear(), n.getMonth(), 1)); setEndDate(n); } },
            { label: 'Geçen Ay', fn: () => { const n = new Date(); setStartDate(new Date(n.getFullYear(), n.getMonth() - 1, 1)); setEndDate(new Date(n.getFullYear(), n.getMonth(), 0)); } },
            { label: 'Son 3 Ay', fn: () => { const n = new Date(); setStartDate(new Date(n.getFullYear(), n.getMonth() - 2, 1)); setEndDate(n); } },
            { label: 'Son 6 Ay', fn: () => { const n = new Date(); setStartDate(new Date(n.getFullYear(), n.getMonth() - 5, 1)); setEndDate(n); } },
            { label: 'Bu Yıl', fn: () => { const n = new Date(); setStartDate(new Date(n.getFullYear(), 0, 1)); setEndDate(n); } },
          ].map(p => (
            <button
              key={p.label}
              onClick={p.fn}
              className="px-2 py-1 rounded-md text-[10px] font-medium bg-slate-50 text-slate-500 border border-slate-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200 transition-colors"
            >
              {p.label}
            </button>
          ))}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-md px-2.5 py-1 shadow-sm">
            <Calendar size={12} className="text-slate-400" />
            <DatePicker
              selected={startDate}
              onChange={d => setStartDate(d)}
              dateFormat="dd MMM yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={5}
              maxDate={endDate}
              className="text-[11px] text-slate-600 bg-transparent border-none outline-none w-[80px] cursor-pointer"
            />
            <span className="text-slate-300 text-[10px]">–</span>
            <DatePicker
              selected={endDate}
              onChange={d => setEndDate(d)}
              dateFormat="dd MMM yyyy"
              showMonthDropdown
              showYearDropdown
              dropdownMode="select"
              yearDropdownItemNumber={5}
              minDate={startDate}
              maxDate={new Date()}
              className="text-[11px] text-slate-600 bg-transparent border-none outline-none w-[80px] cursor-pointer"
            />
          </div>

          <button
            onClick={fetchAllData}
            disabled={loading}
            className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50 shadow-sm transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
            Yenile
          </button>

          <div className="flex items-center bg-white border border-slate-200 rounded-md shadow-sm overflow-hidden text-[11px]">
            <button onClick={expandAll} className="px-2 py-1 text-slate-500 hover:bg-slate-50 transition-colors border-r border-slate-200">Genişlet</button>
            <button onClick={collapseAll} className="px-2 py-1 text-slate-500 hover:bg-slate-50 transition-colors">Daralt</button>
          </div>

          <button className="flex items-center gap-1 bg-white border border-slate-200 rounded-md px-2 py-1 text-[11px] text-slate-500 hover:bg-slate-50 shadow-sm transition-colors">
            <Download size={11} />
            Excel
          </button>
          <button
            onClick={handleExportPDF}
            disabled={exporting}
            className="flex items-center gap-1 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white rounded-md px-2.5 py-1 text-[11px] font-medium shadow-sm transition-colors"
          >
            {exporting ? <Loader2 size={11} className="animate-spin" /> : <FileDown size={11} />}
            {exporting ? 'PDF...' : 'PDF'}
          </button>
        </div>
      </div>

      {/* Tablo */}
      <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden relative">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse min-w-[1100px]">
            <thead>
              <tr>
                <th
                  colSpan={2}
                  className="sticky left-0 z-20 bg-slate-700 px-3 py-2 text-left text-[10px] font-semibold text-slate-300 uppercase tracking-wider"
                  style={{ minWidth: 220 }}
                >
                  Mecra
                </th>
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    className="bg-slate-700 px-2 py-2 text-right text-[10px] font-semibold text-slate-300 uppercase tracking-wider whitespace-nowrap"
                    style={{ minWidth: 80 }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {TABLE_DATA
                .filter(g =>
                  g.platform !== 'Media Satın Alma' ||
                  Object.values(reportData?.['Media Satın Alma'] || {}).some(b => (b?.harcama || 0) > 0)
                )
                .map(g => (
                <PlatformGroup
                  key={g.platform}
                  group={g}
                  data={reportData}
                  collapsedPlatforms={collapsedPlatforms}
                  togglePlatform={togglePlatform}
                  expandedRows={expandedRows}
                  toggleRow={toggleRow}
                />
              ))}
              <TotalRow data={reportData} />
            </tbody>
          </table>
        </div>

        {loading && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-30 rounded-lg">
            <div className="flex items-center gap-2 bg-white px-4 py-2 rounded-md shadow-md border border-slate-200">
              <Loader2 size={14} className="text-slate-500 animate-spin" />
              <span className="text-[11px] text-slate-500">Veriler yükleniyor…</span>
            </div>
          </div>
        )}
      </div>

      <p className="mt-2 text-[10px] text-slate-400 flex items-center gap-1">
        <Filter size={9} />
        Platform satırlarına tıklayarak alt detayları açıp kapatabilirsiniz.
      </p>

      {/* Özet Tablo */}
      <div className="mt-8 mb-4">
        <h2 className="text-base font-bold text-slate-800 tracking-tight mb-1">
          Özet
        </h2>
        <p className="text-[11px] text-slate-400 mb-3">
          Platform bazında toplam performans
        </p>
      </div>
      <SummaryTable data={reportData} />

      {/* Google Search Brand / Non-Brand */}
      <div className="mt-8 mb-4">
        <h2 className="text-base font-bold text-slate-800 tracking-tight mb-1">
          Google Search
        </h2>
        <p className="text-[11px] text-slate-400 mb-3">
          Brand / Non-Brand kırılımı
        </p>
      </div>
      <GoogleSearchTable
        searchData={reportData._googleSearch}
        expandedRows={expandedRows}
        toggleRow={toggleRow}
      />

      {/* Always On Generic */}
      <div className="mt-8 mb-4">
        <h2 className="text-base font-bold text-slate-800 tracking-tight mb-1">
          ALWAYS ON Generic
        </h2>
        <p className="text-[11px] text-slate-400 mb-3">
          Kampanya isminde "AO", "Always On" veya "Always" geçen tüm kampanyalar
        </p>
      </div>
      <AlwaysOnTable
        aoData={reportData._alwaysOn}
        expandedRows={expandedRows}
        toggleRow={toggleRow}
      />

      {/* Always On: Local Store */}
      <div className="mt-8 mb-4">
        <h2 className="text-base font-bold text-slate-800 tracking-tight mb-1">
          Always On: Local Store
        </h2>
        <p className="text-[11px] text-slate-400 mb-3">
          Kampanya isminde "Local Store", "Store Visit", "Local Visit", "Lokal" veya "Mağaza" geçen kampanyalar
        </p>
      </div>
      <AlwaysOnTable
        aoData={reportData._localStore}
        expandedRows={expandedRows}
        toggleRow={toggleRow}
        tableTitle="Always On: Local Store"
        tableId="local"
      />

      {/* Ana Kampanya — kampanya ismine göre filtrele */}
      <div className="mt-8 mb-4">
        <h2 className="text-base font-bold text-slate-800 tracking-tight mb-1">
          Ana Kampanya
        </h2>
        <p className="text-[11px] text-slate-400 mb-3">
          Kampanya ismine göre filtreleme — tüm platformlar (Google, Meta, YouTube, Pinterest, TikTok, Remarketing, Media Buying)
        </p>
      </div>
      <LansmanTable
        id="ana"
        title="ANA KAMPANYA ADI"
        rows={['Google', 'Meta', 'YouTube', 'Pinterest', 'TikTok', 'Remarketing', 'Media Buying']}
        googleRaw={rawDataRef.current.google}
        metaRaw={rawDataRef.current.meta}
        googleAdgroupsRaw={rawDataRef.current.googleAdgroups}
        metaAdsRaw={rawDataRef.current.metaAds}
        reportData={reportData}
        expandedRows={expandedRows}
        toggleRow={toggleRow}
        quickTags={['YBB', 'Renkli', 'Beyaz', 'Branding', 'Performance']}
        tags={campaignTags.ana}
        onTagsChange={t => setCampaignTags(prev => ({ ...prev, ana: typeof t === 'function' ? t(prev.ana) : t }))}
      />

      {[1, 2, 3].map(i => {
        const key = `kamp${i}`;
        return (
          <div key={`kamp-${i}`}>
            <div className="mt-8 mb-4">
              <h2 className="text-base font-bold text-slate-800 tracking-tight mb-1">
                Kampanya {i}
              </h2>
            </div>
            <LansmanTable
              id={key}
              title={`Kampanya ${i}`}
              rows={['Google', 'Meta', 'YouTube', 'Pinterest', 'TikTok', 'Remarketing', 'Media Buying']}
              googleRaw={rawDataRef.current.google}
              metaRaw={rawDataRef.current.meta}
              googleAdgroupsRaw={rawDataRef.current.googleAdgroups}
              metaAdsRaw={rawDataRef.current.metaAds}
              reportData={reportData}
              expandedRows={expandedRows}
              toggleRow={toggleRow}
              tags={campaignTags[key]}
              onTagsChange={t => setCampaignTags(prev => ({ ...prev, [key]: typeof t === 'function' ? t(prev[key]) : t }))}
            />
          </div>
        );
      })}
    </div>
    </MetaReachContext.Provider>
  );
}
